import {
  buttonVariants,
  GenerateBrokerageFirmReportsCard,
  NewCustomerButton,
  PageHeader,
  ReportsForBrokerageFirmList,
} from '@folio/components';
import {
  AccountingDashboardModule,
  ArchivedNewBusinessModule,
  ClaimsListModule,
  CommissionsModule,
  GeneralInboxModule,
  DerogationValidationsModule,
  DocumentsListBrokerageFirmModule,
  FraudControlModule,
  IntermediaryBrokerageFirmListModule,
  IntermediaryDetailsDocumentsModule,
  IntermediaryDetailsModule,
  IntermediaryInformationCardModule,
  IntermediaryProductsModule,
  IntermediaryUserCreationModule,
  IntermediaryUserManagementModule,
  MetabaseAnalyticsModule,
  OngoingNewBusinessModule,
  PortfolioModule,
} from '@folio/modules';
import { IntermediaryDashboardLayout, LogoModule } from '@korint/folio';
import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import {
  brokerageFirmControllerGetBrokerageFirmQueryOptions,
  configControllerGetPublicTenantConfigQueryOptions,
  legacyPermissionsControllerAtMeQueryOptions,
} from 'korint-sdk';
import { useKorintUniqConfig, type IntermediaryPage } from 'korint-sdk/react';
import { PlusIcon } from 'lucide-react';
import { Suspense, lazy, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Stack } from '@folio/components/ui/flex';

const ConversationModule = lazy(() =>
  import('@folio/modules').then(m => ({ default: m.ConversationModule })),
);

export function UniqIntermediaryDashboardLayout({
  pages,
}: {
  pages: ReadonlyArray<IntermediaryPage>;
}) {
  return <IntermediaryDashboardLayout pages={pages} logo={<LogoModule className="h-8 w-auto" />} />;
}

function IntermediaryDashboardContentLayoutRight({ type }: { type: IntermediaryPage['type'] }) {
  const { intermediaryDashboard } = useKorintUniqConfig();
  const { t } = useTranslation();

  if (
    !intermediaryDashboard.hideNewBusinessButton &&
    (type === 'ongoing-new-business' || type === 'portfolio')
  ) {
    return <NewCustomerButton />;
  }
  if (type === 'intermediary-user-list') {
    return (
      <Link to="/intermediary-user-form" className={buttonVariants({ size: 'sm' })}>
        <PlusIcon className="h-4 w-4 mr-2" />
        {t('sidebar.intermediary.userForm')}
      </Link>
    );
  }
  return null;
}

export function IntermediaryDashboardContentLayout({
  title,
  type,
  children,
}: {
  /**
   * Title of the page. If false, no title is displayed (used for pages with children that want a different title).
   */
  title: string | false;
  type: IntermediaryPage['type'];
  children: React.ReactNode;
}) {
  return (
    <Stack>
      {title && (
        <PageHeader title={title} right={<IntermediaryDashboardContentLayoutRight type={type} />} />
      )}
      {children}
    </Stack>
  );
}

export function IntermediaryOnGoingNewBusiness() {
  return <OngoingNewBusinessModule />;
}

export function IntermediaryArchivedNewBusiness() {
  return <ArchivedNewBusinessModule />;
}

export function IntermediaryPortfolio() {
  return <PortfolioModule />;
}

export function IntermediaryReports() {
  const { t } = useTranslation();
  const [reportType, setReportType] = useState<string | undefined>(undefined);

  return (
    <>
      <GenerateBrokerageFirmReportsCard
        title={t('reports.cardTitle')}
        onTypeChange={type => setReportType(type)}
      />

      <ReportsForBrokerageFirmList reportType={reportType ?? ''} />
    </>
  );
}

export function IntermediaryCommissions() {
  return <CommissionsModule />;
}

export function IntermediaryClaims() {
  return <ClaimsListModule />;
}

export function IntermediaryFraudControl() {
  return <FraudControlModule />;
}

export function IntermediaryUsersList() {
  return <IntermediaryUserManagementModule />;
}

export function IntermediaryUserCreation() {
  return <IntermediaryUserCreationModule />;
}

export function IntermediaryAccounting() {
  return <AccountingDashboardModule />;
}

export function IntermediaryBrokerageFirmList() {
  return <IntermediaryBrokerageFirmListModule />;
}

export function IntermediaryDerogations() {
  return <DerogationValidationsModule />;
}

export function IntermediaryProducts() {
  return <IntermediaryProductsModule />;
}

export function IntermediaryDetailsPage() {
  const { intermediaryDetailsPages } = useKorintUniqConfig();

  return (
    <IntermediaryDetailsModule
      informationCards={intermediaryDetailsPages?.intermediaryInformations ?? []}
    />
  );
}

export function IntermediaryMyBrokerageFirm() {
  const { t } = useTranslation();
  const { intermediaryDetailsPages } = useKorintUniqConfig();

  const { data: user } = useSuspenseQuery({
    ...legacyPermissionsControllerAtMeQueryOptions(),
  });

  const brokerageFirmId = user.brokerageFirmId!;

  const {
    data: { firmType },
  } = useSuspenseQuery({
    ...brokerageFirmControllerGetBrokerageFirmQueryOptions(brokerageFirmId),
  });

  const { data: brokerageFirmDocumentsList } = useQuery({
    ...configControllerGetPublicTenantConfigQueryOptions(),
    select: data => data.brokerageFirmDocuments?.brokerageFirmDocumentsList,
  });

  return (
    <Stack>
      {(intermediaryDetailsPages?.intermediaryInformations ?? []).map(({ titleKey, fields }) => (
        <IntermediaryInformationCardModule
          key={titleKey}
          titleKey={titleKey}
          fields={fields}
          brokerageFirmId={brokerageFirmId}
        />
      ))}

      {firmType === 'RETAIL' && (
        <DocumentsListBrokerageFirmModule
          brokerageFirmId={brokerageFirmId}
          documentsList={[
            { category: 'DISTRIBUTION_CONTRACT' },
            ...(brokerageFirmDocumentsList ?? []),
          ]}
          title={t('intermediaryInformationCard.titles.supportingDocuments')}
        />
      )}
    </Stack>
  );
}

export function IntermediaryDetailsDocuments() {
  const { intermediaryDetailsPages } = useKorintUniqConfig();

  return (
    <IntermediaryDetailsDocumentsModule
      extraDocuments={intermediaryDetailsPages?.intermediaryExtraDocuments}
    />
  );
}

export function IntermediaryAnalytics() {
  return <MetabaseAnalyticsModule />;
}

export function IntermediaryGeneralInbox() {
  return <GeneralInboxModule />;
}

export function IntermediaryConversations() {
  const { conversationId } = useParams<{ conversationId?: string }>();
  const navigate = useNavigate();

  return (
    <Suspense>
      <ConversationModule
        activeConversationId={conversationId}
        onConversationSelect={(id: string) => navigate(`/conversations/${id}`)}
        onConversationDeselect={() => navigate('/conversations')}
      />
    </Suspense>
  );
}
